import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import orderBy from 'lodash/orderBy';
import moment from 'moment';

import { useTranslation, Trans } from 'react-i18next';

// ui
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import TableContainer from '@material-ui/core/TableContainer';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Tooltip from '@material-ui/core/Tooltip';

// font and icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine } from '@fortawesome/free-solid-svg-icons';

// components
import { StyledIconButton } from '../../../../components/button/Button';
import TableHeaderEnhance from '../../../../components/table/TableHeaderEnhance';
import areas from '../../../../assets/json/region';
// import areas from '../../../../assets/json/regionDopa';
import DamLargeColor from '../../../../components/color/dam/DamLargeColor';
import DamLargeColorCurve from '../../../../components/color/dam/DamLargeColorCurve';
import DamChartDailyMain from '../../chart/daily/DamChartDailyMain';
// import DamChartDailyCompareMain from '../../chart/compare/DamChartDailyCompareMain';
import DamChartDailyByRegionMain from '../../chart/daily/DamChartDailyByRegionMain';

// utils
import formatNumber from '../../../../utils/number/Number';

// styles
import styles from '../style';
import {
  StyledCommentTableCell,
  StyledFooterSubTableCell,
  StyledFooterTableCell,
  StyledSubTableCell,
} from '../../../../components/table/StyleTable';
import '../../styles.css';

const columns = [
  {
    id: 'dam.dam_name.th',
    disablePadding: false,
    label: <Trans>อ่างเก็บน้ำ</Trans>,
    sortable: true,
  },
  {
    id: 'geocode.province_name.th',
    numeric: false,
    disablePadding: true,
    label: <Trans>จังหวัด</Trans>,
    sortable: true,
  },
  {
    id: 'basin.basin_name.th',
    label: <Trans>ลุ่มน้ำ</Trans>,
    sortable: true,
  },
  {
    id: 'dam.max_storage',
    align: 'right',
    numeric: true,
    disablePadding: false,
    label: <Trans>ที่ รนส.</Trans>,
    unit: <Trans>ล้าน ลบ.ม.</Trans>,
    sortable: true,
  },
  {
    id: 'dam.normal_storage',
    align: 'right',
    numeric: true,
    disablePadding: false,
    label: <Trans>ที่ รนก.</Trans>,
    unit: <Trans>ล้าน ลบ.ม.</Trans>,
    sortable: true,
  },
  {
    id: 'dam.uses_water',
    align: 'right',
    numeric: true,
    disablePadding: false,
    label: <Trans>ที่ น้ำใช้การ</Trans>,
    unit: <Trans>ล้าน ลบ.ม.</Trans>,
    sortable: true,
  },
  {
    id: 'dam_storage_percent',
    align: 'right',
    numeric: true,
    disablePadding: false,
    label: <Trans>ปริมาณน้ำ</Trans>,
    unit: <Trans>ล้าน ลบ.ม (% ความจุ)</Trans>,
    sortable: true,
  },
  {
    id: 'dam_uses_water_percent',
    align: 'right',
    numeric: true,
    disablePadding: false,
    label: <Trans>ปริมาตรใช้การ</Trans>,
    unit: <Trans>ล้าน ลบ.ม (% ความจุ)</Trans>,
    sortable: true,
  },
  {
    id: 'dam_inflow_avg',
    align: 'right',
    numeric: true,
    disablePadding: false,
    label: <Trans>ค่าเฉลี่ยรวมทั้งปี</Trans>,
    unit: <Trans>ล้าน ลบ.ม.</Trans>,
    sortable: true,
  },
  {
    id: 'dam_inflow',
    align: 'right',
    numeric: true,
    disablePadding: false,
    label: <Trans>ปริมาตรน้ำ</Trans>,
    unit: <Trans>ล้าน ลบ.ม.</Trans>,
    sortable: true,
  },
  {
    id: 'dam_inflow_acc',
    align: 'right',
    numeric: true,
    disablePadding: false,
    label: <Trans>สะสมตั้งแต่ 1 ม.ค.</Trans>,
    unit: <Trans>ล้าน ลบ.ม.</Trans>,
    sortable: true,
  },
  {
    id: 'dam_released',
    align: 'right',
    numeric: true,
    disablePadding: false,
    label: <Trans>ปริมาตรน้ำ</Trans>,
    unit: <Trans>ล้าน ลบ.ม.</Trans>,
    sortable: true,
  },
  {
    id: 'dam_released_acc',
    align: 'right',
    numeric: true,
    disablePadding: false,
    label: <Trans>สะสมตั้งแต่ 1 ม.ค.</Trans>,
    unit: <Trans>ล้าน ลบ.ม.</Trans>,
    sortable: true,
  },
  {
    id: 'chart',
    numeric: false,
    disablePadding: false,
    label: '',
  },
];

const useStyles = makeStyles(styles);

export default function DamLargeDataListsRid(props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const { datas, handleModalChanage } = props;
  const filterData = datas.filter((d) => d.agency.id === 12);
  // const unmounted = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const currentDate = moment().format('D/M/2020');

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // table sorting
  const [state, setState] = useState({
    order: 'desc',
    orderBy: 'dam_storage_percent',
  });

  // set total summary
  let grandTotalInflow = 0;
  let grandTotalInflowAcc = 0;
  let grandTotalInflowAvg = 0;
  let grandTotalReleased = 0;
  let grandTotalReleasedAcc = 0;
  let grandTotalDamStorage = 0;
  let grandTotalDamUseWater = 0;
  let grandTotalMaxStorage = 0;
  let grandTotalNormalStorage = 0;
  let grandTotalUsesWater = 0;

  // force modal close
  // useEffect(() => {
  //   if (modalData.open) modalData.open = false;
  //   return () => { unmounted.current = true; };
  // }, [modalData.open]);

  const handleChart = (data) => (e) => {
    e.preventDefault();

    handleModalChanage({
      open: true,
      header: t('กราฟอ่างเก็บน้ำรายวัน กรมชลประทาน'),
      subheader: null,
      content: <DamChartDailyMain id={data} agencyId={12} />,
    });
  };

  // const handleChartCompareDaily = (data) => (e) => {
  //   e.preventDefault();

  //   setModalData({
  //     open: true,
  //     header: 'กราฟเปรียบเทียบอ่างเก็บน้ำรายวัน กรมชลประทาน',
  //     subheader: null,
  //     content: <DamChartDailyCompareMain id={data} agencyId={12} />,
  //   });
  // };

  const handleChartSumByRegion = (regionId) => (e) => {
    e.preventDefault();

    handleModalChanage({
      open: true,
      header: t('กราฟอ่างเก็บน้ำรายวัน รายภาค กรมชลประทาน'),
      subheader: null,
      content: <DamChartDailyByRegionMain id={regionId} />,
    });
  };

  // const handleChartCompareDailySumByRegion = (regionId) => (e) => {
  //   e.preventDefault();

  //   setModalData({
  //     open: true,
  //     header: 'กราฟเปรียบเทียบอ่างเก็บน้ำรายวัน รายภาค กรมชลประทาน',
  //     subheader: null,
  //     content: <DamChartDailyByRegionCompareMain id={regionId} />,
  //   });
  // };

  const handleRequestSort = (event, property) => {
    const orderByProperty = property;
    let orderLocal = 'desc';

    if (state.orderBy === property && state.order === 'desc') {
      orderLocal = 'asc';
    }

    setState({ order: orderLocal, orderBy: orderByProperty });
  };

  return (
    <>
      <Paper className={classes.root}>
        <TableContainer style={{ maxHeight: 650 }}>
          <Table stickyHeader className={classes.table} id="large_dam">
            {/* table header */}
            <TableHead>
              <TableRow>
                <StyledCommentTableCell colSpan={3} align="center">{t('พิ้นที่')}</StyledCommentTableCell>
                <StyledCommentTableCell colSpan={3} align="center">{t('ความจุ')}</StyledCommentTableCell>
                <StyledCommentTableCell colSpan={2} align="center">{t('น้ำในอ่าง')}</StyledCommentTableCell>
                <StyledCommentTableCell colSpan={3} align="center">{t('ปริมาณน้ำไหลลงอ่าง')}</StyledCommentTableCell>
                <StyledCommentTableCell colSpan={2} align="center">{t('ปริมาณน้ำระบาย')}</StyledCommentTableCell>
                <StyledCommentTableCell colSpan={2} align="center" />
              </TableRow>
            </TableHead>
            <TableHeaderEnhance
              firstColumnSticky
              columnData={columns}
              order={state.order}
              orderBy={state.orderBy}
              onRequestSort={handleRequestSort}
            />
            {/* END table header */}

            <TableBody>
              {filterData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={14}>
                    {isLoading ? ( // Check if data is loading
                      <strong>{t('กำลังโหลดข้อมูล...')}</strong>
                    ) : (
                      <strong>{t('ไม่มีข้อมูลแหล่งน้ำขนาดใหญ่ ตามเงื่อนไข')}</strong>
                    )}
                  </TableCell>
                </TableRow>
              )}

              {/* area group title */}
              {areas
                .map((area) => {
                  // summary value by region
                  let totalInflow = 0;
                  let totalInflowAcc = 0;
                  let totalInflowAvg = 0;
                  let totalReleased = 0;
                  let totalReleasedAcc = 0;
                  let totalDamStorage = 0;
                  let totalDamUseWater = 0;
                  let totalMaxStorage = 0;
                  let totalNormalStorage = 0;
                  let totalUsesWater = 0;

                  // count total records for each area
                  let totalDamInArea = 0;
                  if (filterData.length > 0) {
                    totalDamInArea = filterData
                      .filter((row) => row.geocode.area_code === area.id.toString())
                      .length;
                  }
                  if (totalDamInArea === 0) return null;

                  return (
                    <Fragment key={area.id}>
                      <TableRow>
                        <StyledSubTableCell bgcolor="primary" className="first-column-sticky">
                          {t(area.name)}
                          {' '}
                          {`(${totalDamInArea})`}
                        </StyledSubTableCell>
                        <StyledSubTableCell colSpan={14} bgcolor="primary" />
                      </TableRow>

                      {/* data rows */}
                      {orderBy(filterData, state.orderBy, state.order)
                        .filter((row) => row.geocode.area_code === area.id.toString())
                        .map((row) => {
                          const status = DamLargeColorCurve(row.dam_storage, row.dam_storage_percent, row.dam.id, currentDate);

                          totalInflow += row.dam_inflow;
                          totalInflowAcc += row.dam_inflow_acc;
                          totalInflowAvg += row.dam_inflow_avg;
                          totalReleased += row.dam_released;
                          totalReleasedAcc += row.dam_released_acc;
                          totalMaxStorage += row.dam.max_storage;
                          totalNormalStorage += row.dam.normal_storage;
                          totalUsesWater += row.dam.uses_water;
                          totalDamStorage += row.dam_storage;
                          totalDamUseWater += row.dam_uses_water;

                          grandTotalInflow += row.dam_inflow;
                          grandTotalInflowAvg += row.dam_inflow_avg;
                          grandTotalInflowAcc += row.dam_inflow_acc;
                          grandTotalReleased += row.dam_released;
                          grandTotalReleasedAcc += row.dam_released_acc;
                          grandTotalDamStorage += row.dam_storage;
                          grandTotalDamUseWater += row.dam_uses_water;
                          grandTotalMaxStorage += row.dam.max_storage;
                          grandTotalNormalStorage += row.dam.normal_storage;
                          grandTotalUsesWater += row.dam.uses_water;

                          return (
                            <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                              <TableCell className="first-column-sticky">
                                {row.dam.dam_name.th}
                              </TableCell>
                              <TableCell>
                                {row.geocode.province_name.th}
                              </TableCell>
                              <TableCell>
                                {row.basin.basin_name.th.replace('ลุ่มน้ำ', '')}
                              </TableCell>
                              <TableCell align="right">
                                {formatNumber(row.dam.max_storage)}
                              </TableCell>
                              <TableCell align="right">
                                {formatNumber(row.dam.normal_storage)}
                              </TableCell>
                              <TableCell align="right">
                                {formatNumber(row.dam.uses_water)}
                              </TableCell>
                              <TableCell align="right">
                                <span>
                                  {formatNumber(row.dam_storage)}
                                  {' '}
                                  <span style={{ color: status.color.statusColor }}>
                                    (
                                    {row.dam_storage_percent.toFixed(0)}
                                    %)
                                  </span>
                                </span>
                              </TableCell>
                              <TableCell align="right">
                                {row.dam_uses_water_percent > 0 ? (
                                  <div>
                                    {`${formatNumber(row.dam_uses_water.toFixed(0))} (${row.dam_uses_water_percent_calc.toFixed(0)}%)`}
                                  </div>
                                ) : (
                                  <div>-</div>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                {row.dam_inflow_avg === 0
                                  && '-'}
                                {row.dam_inflow_avg > 0
                                  && formatNumber(row.dam_inflow_avg)}
                              </TableCell>
                              <TableCell align="right">
                                {row.dam_inflow.toFixed(2)}
                              </TableCell>
                              <TableCell align="right">
                                {row.dam_inflow_acc === 0
                                  && '-'}
                                {row.dam_inflow_acc > 0
                                  && `${formatNumber(row.dam_inflow_acc.toFixed(0))} (${row.dam_inflow_acc_percent.toFixed(0)}%)`}
                              </TableCell>
                              <TableCell align="right">
                                {row.dam_released.toFixed(2)}
                              </TableCell>
                              <TableCell align="right">
                                {row.dam_released_acc === 0
                                  && '-'}
                                {row.dam_released_acc > 0
                                  && formatNumber(row.dam_released_acc.toFixed(2))}
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title={t('กราฟรายปี')}>
                                  <StyledIconButton onClick={handleChart(row.dam.id)}>
                                    <FontAwesomeIcon icon={faChartLine} style={{ color: '#888' }} />
                                  </StyledIconButton>
                                </Tooltip>
                                {/* <Tooltip title="กราฟเปรียบเทียบรายวัน">
                                  <StyledIconButton onClick={handleChartCompareDaily(row.dam.id)}>
                                    <FontAwesomeIcon icon={faChartBar} style={{ color: '#888' }} />
                                  </StyledIconButton>
                                </Tooltip> */}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      {/* END data rows */}

                      {/* sum by each region */}
                      <TableRow>
                        <StyledFooterTableCell className="first-column-sticky">
                          <Box fontWeight="bold">
                            {t(`รวม${area.name}`)}
                          </Box>
                        </StyledFooterTableCell>
                        <StyledFooterTableCell />
                        <StyledFooterTableCell />
                        <StyledFooterTableCell align="right">
                          <strong>
                            {!Number.isNaN(totalMaxStorage, 10) ? formatNumber(totalMaxStorage.toFixed(0)) : '-'}
                          </strong>
                        </StyledFooterTableCell>
                        <StyledFooterTableCell align="right">
                          <strong>
                            {formatNumber(totalNormalStorage.toFixed(0))}
                          </strong>
                        </StyledFooterTableCell>
                        <StyledFooterTableCell align="right">
                          <strong>
                            {!Number.isNaN(totalUsesWater, 10) ? formatNumber(totalUsesWater.toFixed(0)) : '-'}
                          </strong>
                        </StyledFooterTableCell>
                        <StyledFooterTableCell align="right">
                          <strong>
                            {formatNumber(totalDamStorage.toFixed(0))}
                            <span
                              style={{
                                color: DamLargeColor(
                                  parseFloat((totalDamStorage * 100) / totalMaxStorage).toFixed(0),
                                ).color.statusColor,
                              }}
                            >
                              {' ('}
                              {!Number.isNaN(totalMaxStorage, 10) ? ((totalDamStorage * 100) / totalMaxStorage).toFixed(0) : '-'}
                              {'%)'}
                            </span>
                          </strong>
                        </StyledFooterTableCell>
                        <StyledFooterTableCell align="right">
                          <strong>
                            {formatNumber(totalDamUseWater.toFixed(0))}
                            {' ('}
                            {!Number.isNaN(totalMaxStorage, 10) ? ((totalDamUseWater * 100) / totalMaxStorage).toFixed(0) : '-'}
                            {'%)'}
                          </strong>
                        </StyledFooterTableCell>
                        <StyledFooterTableCell align="right">
                          <strong>
                            {formatNumber(totalInflowAvg.toFixed(0))}
                          </strong>
                        </StyledFooterTableCell>
                        <StyledFooterTableCell align="right">
                          <strong>
                            {totalInflow.toFixed(2)}
                          </strong>
                        </StyledFooterTableCell>
                        <StyledFooterTableCell align="right">
                          <strong>
                            {formatNumber(totalInflowAcc.toFixed(2))}
                            {' ('}
                            {((100 * totalInflowAcc) / totalInflowAvg).toFixed(2)}
                            {'%)'}
                          </strong>
                        </StyledFooterTableCell>
                        <StyledFooterTableCell align="right">
                          <strong>
                            {totalReleased.toFixed(2)}
                          </strong>
                        </StyledFooterTableCell>
                        <StyledFooterTableCell align="right">
                          <strong>
                            {totalReleasedAcc.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          </strong>
                        </StyledFooterTableCell>
                        <StyledFooterTableCell align="center">
                          <Tooltip title={t('กราฟรายปี')}>
                            <StyledIconButton onClick={handleChartSumByRegion(area.id)}>
                              <FontAwesomeIcon icon={faChartLine} style={{ color: '#888' }} />
                            </StyledIconButton>
                          </Tooltip>
                          {/* <Tooltip title="กราฟเปรียบเทียบรายวัน">
                            <StyledIconButton onClick={handleChartCompareDailySumByRegion(area.id)}>
                              <FontAwesomeIcon icon={faChartBar} style={{ color: '#888' }} />
                            </StyledIconButton>
                          </Tooltip> */}
                        </StyledFooterTableCell>
                      </TableRow>
                      {/* END sum by each region */}
                    </Fragment>
                  );
                })}
              {/* END area group title */}

              {/* grand total all regions */}
              {filterData.length !== 0 && (
                <TableRow>
                  <StyledFooterSubTableCell className="first-column-sticky">
                    <Box fontWeight="bold">{t('รวมทั้งประเทศ')}</Box>
                  </StyledFooterSubTableCell>
                  <StyledFooterSubTableCell />
                  <StyledFooterSubTableCell />
                  <StyledFooterSubTableCell align="right">
                    <strong>
                      {!Number.isNaN(grandTotalMaxStorage, 10) ? formatNumber(grandTotalMaxStorage.toFixed(0)) : '-'}
                    </strong>
                  </StyledFooterSubTableCell>
                  <StyledFooterSubTableCell align="right">
                    <strong>
                      {formatNumber(grandTotalNormalStorage.toFixed(0))}
                    </strong>
                  </StyledFooterSubTableCell>
                  <StyledFooterSubTableCell align="right">
                    <strong>
                      {!Number.isNaN(grandTotalUsesWater, 10) ? formatNumber(grandTotalUsesWater.toFixed(0)) : '-'}
                    </strong>
                  </StyledFooterSubTableCell>
                  <StyledFooterSubTableCell align="right">
                    <strong>
                      {formatNumber(grandTotalDamStorage.toFixed(0))}
                      <span style={{
                        color: DamLargeColor(
                          (grandTotalDamStorage * 100) / grandTotalMaxStorage,
                        ).color.statusColor,
                      }}
                      >
                        {' ('}
                        {!Number.isNaN(grandTotalMaxStorage, 10) ? ((grandTotalDamStorage * 100) / grandTotalMaxStorage).toFixed(0) : '-'}
                        {'%)'}
                      </span>
                    </strong>
                  </StyledFooterSubTableCell>
                  <StyledFooterSubTableCell align="right">
                    <strong>
                      {formatNumber(grandTotalDamUseWater.toFixed(0))}
                      {' ('}
                      {!Number.isNaN(grandTotalMaxStorage, 10) ? ((grandTotalDamUseWater * 100) / grandTotalMaxStorage).toFixed(0) : '-'}
                      {'%)'}
                    </strong>
                  </StyledFooterSubTableCell>
                  <StyledFooterSubTableCell align="right">
                    <strong>
                      {formatNumber(grandTotalInflowAvg.toFixed(0))}
                    </strong>
                  </StyledFooterSubTableCell>
                  <StyledFooterSubTableCell align="right">
                    <strong>
                      {formatNumber(grandTotalInflow.toFixed(2))}
                    </strong>
                  </StyledFooterSubTableCell>
                  <StyledFooterSubTableCell align="right">
                    <strong>
                      {formatNumber(grandTotalInflowAcc.toFixed(2))}
                      {' ('}
                      {((100 * grandTotalInflowAcc) / grandTotalInflowAvg).toFixed(2)}
                      {'%)'}
                    </strong>
                  </StyledFooterSubTableCell>
                  <StyledFooterSubTableCell align="right">
                    <strong>
                      {formatNumber(grandTotalReleased.toFixed(2))}
                    </strong>
                  </StyledFooterSubTableCell>
                  <StyledFooterSubTableCell align="right">
                    <strong>
                      {formatNumber(grandTotalReleasedAcc.toFixed(2))}
                    </strong>
                  </StyledFooterSubTableCell>
                  <StyledFooterSubTableCell align="center">
                    <Tooltip title={t('กราฟรายปี')}>
                      <StyledIconButton onClick={handleChartSumByRegion(0)}>
                        <FontAwesomeIcon icon={faChartLine} style={{ color: '#888' }} />
                      </StyledIconButton>
                    </Tooltip>
                    {/* <Tooltip title="กราฟเปรียบเทียบรายวัน">
                      <StyledIconButton onClick={handleChartCompareDailySumByRegion(0)}>
                        <FontAwesomeIcon icon={faChartBar} style={{ color: '#888' }} />
                      </StyledIconButton>
                    </Tooltip> */}
                  </StyledFooterSubTableCell>
                </TableRow>
              )}
              {/* END grand total all regions */}

            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
}

DamLargeDataListsRid.propTypes = {
  datas: PropTypes.instanceOf(Array).isRequired,
  handleModalChanage: PropTypes.func.isRequired,
};
